import { useEffect, useState } from 'react';
import axios from 'axios';
import { installProfile as installProfileUrl } from './BaseURL';

// ============================================================================
//  useInstallProfile — what kind of installation is the frontend talking to?
// ============================================================================
//
//    facility   a hospital running MEFIMS for its own operations
//    bureau     Gigatt Apex doing claims work FOR other facilities
//
//  Used to decide which departments to offer. A claims bureau has no wards and
//  no pharmacy; a district hospital has no use for the facility-onboarding
//  screens. Before this, every install saw every department whether or not the
//  tables behind them held anything.
//
//  FETCHED ONCE AND SHARED. The dashboard, the sidebar and the Claims screens
//  all want the same answer, and a hook that re-fetched per consumer would fire
//  the same request three times on every navigation. The promise is memoised at
//  module scope so the second caller waits on the first.
//
//  FAILS AS A FACILITY. If the request fails — an older backend without the
//  endpoint, a network blip — the answer is the ordinary hospital experience
//  with every department visible. Never the reverse: guessing bureau would hide
//  most of the app from a hospital that paid for it.

const FALLBACK = {
  mode: 'facility',
  claimModel: 'flat',
  departments: null,      // null = no restriction
  isBureau: false,
  loading: false,
  degraded: true,
};

let cached = null;
let inFlight = null;

const fetchProfile = () => {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = axios.get(installProfileUrl, { timeout: 8000 })
    .then(({ data }) => {
      cached = {
        mode: data?.mode === 'bureau' ? 'bureau' : 'facility',
        claimModel: data?.claimModel === 'normalised' ? 'normalised' : 'flat',
        departments: Array.isArray(data?.departments) ? data.departments : null,
        isBureau: data?.mode === 'bureau',
        loading: false,
        degraded: !!data?.degraded,
      };
      return cached;
    })
    .catch(() => {
      // Not cached: a transient failure should not pin the fallback for the
      // life of the tab. The next consumer retries.
      inFlight = null;
      return FALLBACK;
    });

  return inFlight;
};

// Call after changing the profile, so the next read re-fetches.
export const clearInstallProfileCache = () => { cached = null; inFlight = null; };

export default function useInstallProfile() {
  const [profile, setProfile] = useState(cached || { ...FALLBACK, loading: !cached, degraded: false });

  useEffect(() => {
    let alive = true;
    fetchProfile().then((p) => { if (alive) setProfile(p); });
    return () => { alive = false; };
  }, []);

  return profile;
}

// Is a department enabled on this installation? `null` departments means all.
// Exported separately so non-component code can ask the same question.
export const isDepartmentEnabled = (profile, departmentName) => {
  if (!profile || !Array.isArray(profile.departments)) return true;
  return profile.departments.includes(departmentName);
};
